<script setup>
import { Avatar, Icon, Typography, Stack, TextButton, Tooltip } from '@aryeo/runway'
import { Menu, MenuButton, MenuItems } from '@headlessui/vue'
import { BellIcon, UserCircleIcon, LogoutIcon } from '@heroicons/vue/solid'

import { useAuth } from '@/Hooks/Company/useAuth'
import { useImpersonation } from '@/Hooks/Company/useImpersonation'

import Badge from '@/Runway/Elements/Badge.vue'
import CsrfToken from '@/Components/Shared/CsrfToken.vue'
import ItemDisabled from '@/Components/Shared/Navigation/ItemDisabled.vue'
import UserMenuItem from '@/Components/Shared/Header/UserMenuItem.vue'

const { authCompanyUser, authCompanyTeamMember, authOrganization } = useAuth()
const { impersonation, stopImpersonating } = useImpersonation()
</script>

<template>
  <Menu as="div" class="relative inline-block my-auto text-left">
    <div>
      <MenuButton
        class="flex items-center justify-center h-10 px-3 text-sm rounded-xl focus:outline-none focus-visible:shadow-focus"
        :class="impersonation ? 'text-amber-400' : 'text-black'"
      >
        <Tooltip v-if="impersonation" class="block mr-3" text="Impersonation Mode Active">
          <Badge text="IM" color="warning" />
        </Tooltip>

        <div class="flex items-center gap-3">
          <Avatar
            :firstName="authCompanyUser.first_name"
            :lastName="authCompanyUser.last_name"
            :image="authCompanyUser.avatar_url"
            size="xsmall"
          />
          <span class="block font-bold truncate">
            <span class="sr-only">Open user menu for </span> {{ authCompanyUser.first_name }}
            {{ authCompanyUser.last_name }}
          </span>

          <Icon name="chevronDown" size="medium" />
        </div>
      </MenuButton>
    </div>

    <transition
      enter-active-class="transition duration-100 ease-out"
      enter-from-class="scale-95 opacity-0"
      enter-to-class="scale-100 opacity-100"
      leave-active-class="transition duration-75 ease-in"
      leave-from-class="scale-100 opacity-100"
      leave-to-class="scale-95 opacity-0"
    >
      <MenuItems
        class="absolute right-0 z-30 w-56 mt-1 origin-top-right bg-white rounded-xl shadow-card border p-2"
      >
        <div v-if="impersonation" class="p-2">
          <div class="p-4 rounded-md bg-background border border-border-light">
            <Stack space="medium">
              <Stack space="xxsmall">
                <Typography variant="body-small">
                  You are currently in impersonation mode, viewing as:
                </Typography>
                <div
                  class="w-full flex items-center justify-between h-10 px-3 text-sm rounded-md border bg-background-light border-border-light"
                >
                  <Typography variant="label">
                    {{ authCompanyTeamMember.company_user.first_name }}
                    {{ authCompanyTeamMember.company_user.last_name }}
                  </Typography>
                  <Avatar
                    :firstName="authCompanyTeamMember.company_user.first_name"
                    :lastName="authCompanyTeamMember.company_user.last_name"
                    :image="authCompanyTeamMember.company_user.avatar_url"
                    size="xsmall"
                  />
                </div>
              </Stack>
              <TextButton
                variant="body-small"
                @click="stopImpersonating"
                text="Exit Impersonation"
                icon-name="signOut"
              />
            </Stack>
          </div>
        </div>
        <div>
          <UserMenuItem :href="route('web.company.auth-company-user.show')">
            <UserCircleIcon class="w-5 h-5 mr-3" aria-hidden="true" />
            Edit User
          </UserMenuItem>
          <template v-if="authOrganization.is_pro_account">
            <UserMenuItem :href="route('web.company.notifications.preferences.edit')">
              <BellIcon class="w-5 h-5 mr-3" aria-hidden="true" />
              Notification Preferences
            </UserMenuItem>
          </template>
          <ItemDisabled
            v-else
            name="Notification Preferences"
            :iconComponent="BellIcon"
            placement="left"
          />
        </div>
        <div v-if="!impersonation">
          <form method="post" :action="route('web.company.login.destroy')">
            <CsrfToken />
            <UserMenuItem as="button" type="submit">
              <LogoutIcon class="w-5 h-5 mr-3 shrink-0" aria-hidden="true" />
              Logout
            </UserMenuItem>
          </form>
        </div>
      </MenuItems>
    </transition>
  </Menu>
</template>
